import { useEffect, useMemo, useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { ContactShadows } from "@react-three/drei";
import { LyherCube } from "./LyherCube";
import { scrollStore } from "@/lib/scrollStore";

const IS_MOBILE =
    typeof window !== "undefined" && window.innerWidth < 1024;

function hasWebGL() {
    try {
        const c = document.createElement("canvas");

        return !!(
            window.WebGLRenderingContext &&
            (
                c.getContext("webgl") ||
                c.getContext("experimental-webgl")
            )
        );
    } catch (e) {
        return false;
    }
}

function prefersReducedMotion() {
    return (
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
}

function ScrollFallback() {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        let raf;

        const loop = () => {
            const p = scrollStore.exp;
            const isOpen = p > 0.26 && p < 0.6;

            setOpen((prev) =>
                prev === isOpen ? prev : isOpen
            );

            raf = requestAnimationFrame(loop);
        };

        raf = requestAnimationFrame(loop);

        return () => cancelAnimationFrame(raf);
    }, []);

    return (
        <div
            className="fixed inset-0 z-0 flex items-center justify-center bg-white"
            role="img"
            aria-label="LYHER Oral Fluid Drug Test Cube"
        >
            <img
                src="/assets/cube-closed.webp"
                alt="LYHER Oral Fluid Multi-Drug Test Kit para test de drogas en saliva"
                width="512"
                height="512"
                data-testid="webgl-fallback-image"
                className={`absolute max-h-[70vh] floaty transition-opacity duration-700 ${
                    open ? "opacity-0" : "opacity-100"
                }`}
            />

            <img
                src="/assets/cube-open.webp"
                alt="Test LYHER de fluido oral abierto mostrando tiras de análisis"
                width="512"
                height="512"
                className={`absolute max-h-[70vh] floaty transition-opacity duration-700 ${
                    open ? "opacity-100" : "opacity-0"
                }`}
            />
        </div>
    );
}

export function ProductStage() {
    const ok = useMemo(
        () => hasWebGL() && !prefersReducedMotion(),
        []
    );

    if (!ok) {
        return <ScrollFallback />;
    }

    return (
        <div
            className="fixed inset-0 z-0 bg-white"
            aria-hidden="true"
            data-testid="product-stage"
        >
            <Canvas
                dpr={IS_MOBILE ? [1, 1.5] : [1, 1.8]}
                camera={{
                    position: [0, 0.2, 8.8],
                    fov: 38,
                }}
                gl={{
                    antialias: !IS_MOBILE,
                    alpha: true,
                    powerPreference: "high-performance",
                }}
            >
                <ambientLight intensity={0.9} />

                <directionalLight
                    position={[5, 6, 6]}
                    intensity={1.4}
                />

                <directionalLight
                    position={[-6, 3, -4]}
                    intensity={0.5}
                />

                <LyherCube mode="scroll" />

                {!IS_MOBILE && (
                    <ContactShadows
                        position={[0, -3.1, 0]}
                        opacity={0.22}
                        scale={14}
                        blur={2.6}
                        far={5}
                    />
                )}
            </Canvas>
        </div>
    );
}

export function FloatingCube({ className = "" }) {
    const ok = useMemo(
        () => hasWebGL() && !prefersReducedMotion(),
        []
    );

    const wrapRef = useRef(null);
    const [active, setActive] = useState(false);

    useEffect(() => {
        const el = wrapRef.current;

        if (!el) return undefined;

        const ob = new IntersectionObserver(
            ([entry]) => {
                setActive(entry.isIntersecting);
            },
            {
                threshold: 0.05,
            }
        );

        ob.observe(el);

        return () => ob.disconnect();
    }, []);

    if (!ok) {
        return (
            <div
                className={`flex items-center justify-center ${className}`}
            >
                <img
                    src="/assets/cube-closed.webp"
                    alt="LYHER Oral Fluid Multi-Drug Test Kit para test de drogas en saliva"
                    width="512"
                    height="512"
                    className="max-h-full floaty"
                />
            </div>
        );
    }

    return (
        <div
            ref={wrapRef}
            className={className}
            data-testid="floating-cube"
        >
            <Canvas
                dpr={IS_MOBILE ? [1, 1.5] : [1, 1.5]}
                frameloop={active ? "always" : "never"}
                camera={{
                    position: [0, 0.3, 8.2],
                    fov: 40,
                }}
                gl={{
                    antialias: !IS_MOBILE,
                    alpha: true,
                    powerPreference: "high-performance",
                }}
            >
                <ambientLight intensity={0.85} />

                <directionalLight
                    position={[4, 5, 5]}
                    intensity={1.3}
                />

                <directionalLight
                    position={[-5, 2, -3]}
                    intensity={0.4}
                />

                <LyherCube mode="float" />
            </Canvas>
        </div>
    );
}